import React from 'react';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import type { ThemeConfig } from '../../types/forms';
import { DEFAULT_THEME_CONFIG } from '../../types/forms';
import { BUTTON_SIZE_PRESETS } from '../../lib/themeUtils';

interface ThemeSettingsProps {
  themeConfig: ThemeConfig;
  onUpdate: (config: ThemeConfig) => void;
}

const GOOGLE_FONTS = [
  'Inter',
  'Roboto',
  'Open Sans',
  'Lato',
  'Montserrat',
  'Poppins',
  'Raleway',
  'Nunito',
  'Playfair Display',
  'Source Sans 3',
];

export const ThemeSettings: React.FC<ThemeSettingsProps> = ({
  themeConfig,
  onUpdate,
}) => {
  const handleChange = (key: keyof ThemeConfig, value: string | number) => {
    onUpdate({ ...themeConfig, [key]: value });
  };

  return (
    <div className="space-y-8">
      {/* Background */}
      <div className="space-y-4">
        <p className="text-xs text-neutral-500 uppercase tracking-wider">Fundo do formulário</p>
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={themeConfig.backgroundColor}
            onChange={(e) => handleChange('backgroundColor', e.target.value)}
            className="w-10 h-10 min-w-11 min-h-11 sm:min-w-0 sm:min-h-0 rounded-lg border border-neutral-700 cursor-pointer bg-transparent"
          />
          <Input
            value={themeConfig.backgroundColor}
            onChange={(e) => handleChange('backgroundColor', e.target.value)}
            placeholder="#0a0a0a"
            className="flex-1"
          />
        </div>
      </div>

      {/* Typography */}
      <div className="space-y-4">
        <p className="text-xs text-neutral-500 uppercase tracking-wider">Tipografia</p>
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={themeConfig.textColor || '#ffffff'}
            onChange={(e) => handleChange('textColor', e.target.value)}
            className="w-10 h-10 min-w-11 min-h-11 sm:min-w-0 sm:min-h-0 rounded-lg border border-neutral-700 cursor-pointer bg-transparent"
          />
          <Input
            label="Cor da fonte"
            value={themeConfig.textColor || '#ffffff'}
            onChange={(e) => handleChange('textColor', e.target.value)}
            placeholder="#ffffff"
            className="flex-1"
          />
        </div>
        <Select
          label="Familia tipografica"
          value={themeConfig.fontFamily}
          onChange={(e) => handleChange('fontFamily', e.target.value)}
          options={GOOGLE_FONTS.map(font => ({ value: font, label: font }))}
        />
      </div>

      {/* Buttons */}
      <div className="space-y-4">
        <p className="text-xs text-neutral-500 uppercase tracking-wider">Botoes</p>

        {/* Button bg color */}
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={themeConfig.buttonBgColor}
            onChange={(e) => handleChange('buttonBgColor', e.target.value)}
            className="w-10 h-10 min-w-11 min-h-11 sm:min-w-0 sm:min-h-0 rounded-lg border border-neutral-700 cursor-pointer bg-transparent"
          />
          <Input
            label="Cor de fundo"
            value={themeConfig.buttonBgColor}
            onChange={(e) => handleChange('buttonBgColor', e.target.value)}
            className="flex-1"
          />
        </div>

        {/* Button text color */}
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={themeConfig.buttonTextColor}
            onChange={(e) => handleChange('buttonTextColor', e.target.value)}
            className="w-10 h-10 min-w-11 min-h-11 sm:min-w-0 sm:min-h-0 rounded-lg border border-neutral-700 cursor-pointer bg-transparent"
          />
          <Input
            label="Cor do texto"
            value={themeConfig.buttonTextColor}
            onChange={(e) => handleChange('buttonTextColor', e.target.value)}
            className="flex-1"
          />
        </div>

        {/* Border radius presets */}
        <div>
          <label className="block text-sm font-medium text-neutral-200 mb-2">
            Formato do botão
          </label>
          <div className="flex gap-2">
            {([
              { label: 'Reto', value: 0 },
              { label: 'Arredondado', value: 12 },
              { label: 'Pílula', value: 9999 },
            ] as const).map(preset => (
              <button
                key={preset.value}
                onClick={() => handleChange('buttonBorderRadius', preset.value)}
                className={`flex-1 px-3 py-2 rounded-lg text-sm transition-colors ${
                  themeConfig.buttonBorderRadius === preset.value
                    ? 'bg-white/10 text-white ring-1 ring-primary-500'
                    : 'text-neutral-400 hover:text-white bg-neutral-800/50'
                }`}
              >
                <div
                  className="mx-auto mb-1.5 w-14 h-7 flex items-center justify-center text-[10px]"
                  style={{
                    borderRadius: `${Math.min(preset.value, 24)}px`,
                    backgroundColor: themeConfig.buttonBgColor,
                    color: themeConfig.buttonTextColor,
                  }}
                >
                  Botão
                </div>
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        {/* Button style toggle */}
        <div>
          <label className="block text-sm font-medium text-neutral-200 mb-2">Estilo do botao</label>
          <div className="grid grid-cols-3 gap-2">
            {(['filled', 'outlined', 'ghost'] as const).map((style) => (
              <button
                key={style}
                type="button"
                onClick={() => handleChange('buttonStyle', style)}
                className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                  themeConfig.buttonStyle === style
                    ? 'border-primary-500 bg-primary-500/10 text-primary-400'
                    : 'border-neutral-700 text-neutral-400 hover:border-neutral-600'
                }`}
              >
                <div
                  className="mx-auto mb-2 w-16 h-8 flex items-center justify-center text-xs rounded"
                  style={{
                    borderRadius: `${themeConfig.buttonBorderRadius}px`,
                    ...(style === 'filled' && {
                      backgroundColor: themeConfig.buttonBgColor,
                      color: themeConfig.buttonTextColor,
                    }),
                    ...(style === 'outlined' && {
                      border: `2px solid ${themeConfig.buttonBgColor}`,
                      color: themeConfig.buttonBgColor,
                      backgroundColor: 'transparent',
                    }),
                    ...(style === 'ghost' && {
                      color: themeConfig.buttonBgColor,
                      backgroundColor: `${themeConfig.buttonBgColor}15`,
                    }),
                  }}
                >
                  OK
                </div>
                {style === 'filled' ? 'Solido' : style === 'outlined' ? 'Contorno' : 'Ghost'}
              </button>
            ))}
          </div>
        </div>

        {/* Button size preset toggle */}
        {(() => {
          const activeSize = themeConfig.buttonSize ?? 'md';
          const activePreset = BUTTON_SIZE_PRESETS[activeSize];
          const paddingXValue = themeConfig.buttonPaddingX ?? activePreset.paddingX;
          const paddingYValue = themeConfig.buttonPaddingY ?? activePreset.paddingY;
          return (
            <>
              <div>
                <label className="block text-sm font-medium text-neutral-200 mb-2">Tamanho do botão</label>
                <div className="grid grid-cols-3 gap-2">
                  {([
                    { value: 'sm' as const, label: 'Compacto' },
                    { value: 'md' as const, label: 'Médio' },
                    { value: 'lg' as const, label: 'Grande' },
                  ]).map(({ value, label }) => {
                    const preset = BUTTON_SIZE_PRESETS[value];
                    const isActive = activeSize === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => handleChange('buttonSize', value)}
                        className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                          isActive
                            ? 'border-primary-500 bg-primary-500/10 text-primary-400'
                            : 'border-neutral-700 text-neutral-400 hover:border-neutral-600'
                        }`}
                      >
                        <div
                          className="mx-auto mb-2 inline-flex items-center justify-center"
                          style={{
                            padding: `${preset.paddingY / 2}px ${preset.paddingX / 2}px`,
                            fontSize: `${Math.max(10, preset.fontSize - 4)}px`,
                            minHeight: `${preset.minHeight / 2}px`,
                            borderRadius: `${Math.min(themeConfig.buttonBorderRadius, 24)}px`,
                            backgroundColor: themeConfig.buttonBgColor,
                            color: themeConfig.buttonTextColor,
                          }}
                        >
                          OK
                        </div>
                        <div>{label}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Padding horizontal slider */}
              <div>
                <label className="block text-sm font-medium text-neutral-200 mb-2">
                  Padding horizontal: {paddingXValue}px
                </label>
                <input
                  type="range"
                  min={8}
                  max={48}
                  step={2}
                  value={paddingXValue}
                  onChange={(e) => handleChange('buttonPaddingX', Number(e.target.value))}
                  className="w-full accent-primary-500"
                />
                <div className="flex justify-between text-xs text-neutral-500 mt-1">
                  <span>8</span>
                  <span>48</span>
                </div>
              </div>

              {/* Padding vertical slider */}
              <div>
                <label className="block text-sm font-medium text-neutral-200 mb-2">
                  Padding vertical: {paddingYValue}px
                </label>
                <input
                  type="range"
                  min={4}
                  max={32}
                  step={2}
                  value={paddingYValue}
                  onChange={(e) => handleChange('buttonPaddingY', Number(e.target.value))}
                  className="w-full accent-primary-500"
                />
                <div className="flex justify-between text-xs text-neutral-500 mt-1">
                  <span>4</span>
                  <span>32</span>
                </div>
              </div>
            </>
          );
        })()}
      </div>

      {/* Input Fields */}
      <div className="space-y-4">
        <p className="text-xs text-neutral-500 uppercase tracking-wider">Campos de entrada</p>

        {/* Input style toggle */}
        <div>
          <label className="block text-sm font-medium text-neutral-200 mb-2">Estilo do campo</label>
          <div className="grid grid-cols-3 gap-2">
            {(['outlined', 'underline', 'filled'] as const).map((style) => {
              const borderColor = themeConfig.inputBorderColor || `${themeConfig.textColor || '#ffffff'}4D`;
              const bgColor = themeConfig.inputBgColor || 'transparent';
              const radius = themeConfig.inputBorderRadius ?? 12;
              return (
                <button
                  key={style}
                  type="button"
                  onClick={() => handleChange('inputStyle', style)}
                  className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                    (themeConfig.inputStyle || 'outlined') === style
                      ? 'border-primary-500 bg-primary-500/10 text-primary-400'
                      : 'border-neutral-700 text-neutral-400 hover:border-neutral-600'
                  }`}
                >
                  <div
                    className="mx-auto mb-2 w-full h-8 flex items-center px-2 text-xs"
                    style={{
                      color: themeConfig.textColor || '#ffffff',
                      ...(style === 'outlined' && {
                        border: `1px solid ${borderColor}`,
                        borderRadius: `${radius}px`,
                        backgroundColor: bgColor === 'transparent' ? 'transparent' : bgColor,
                      }),
                      ...(style === 'underline' && {
                        borderBottom: `2px solid ${borderColor}`,
                        backgroundColor: 'transparent',
                      }),
                      ...(style === 'filled' && {
                        backgroundColor: bgColor === 'transparent'
                          ? `${themeConfig.textColor || '#ffffff'}0D`
                          : bgColor,
                        borderBottom: `2px solid ${borderColor}`,
                        borderRadius: `${radius}px ${radius}px 0 0`,
                      }),
                    }}
                  >
                    <span style={{ opacity: 0.4 }}>Abc</span>
                  </div>
                  {style === 'outlined' ? 'Contorno' : style === 'underline' ? 'Linha' : 'Preenchido'}
                </button>
              );
            })}
          </div>
        </div>

        {/* Input bg color */}
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={themeConfig.inputBgColor === 'transparent' || !themeConfig.inputBgColor ? '#0a0a0a' : themeConfig.inputBgColor}
            onChange={(e) => handleChange('inputBgColor', e.target.value)}
            className="w-10 h-10 min-w-11 min-h-11 sm:min-w-0 sm:min-h-0 rounded-lg border border-neutral-700 cursor-pointer bg-transparent"
          />
          <Input
            label="Cor de fundo do campo"
            value={themeConfig.inputBgColor || 'transparent'}
            onChange={(e) => handleChange('inputBgColor', e.target.value)}
            placeholder="transparent"
            className="flex-1"
          />
        </div>

        {/* Input border color */}
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={themeConfig.inputBorderColor || (themeConfig.textColor || '#ffffff')}
            onChange={(e) => handleChange('inputBorderColor', e.target.value)}
            className="w-10 h-10 min-w-11 min-h-11 sm:min-w-0 sm:min-h-0 rounded-lg border border-neutral-700 cursor-pointer bg-transparent"
          />
          <Input
            label="Cor da borda"
            value={themeConfig.inputBorderColor || ''}
            onChange={(e) => handleChange('inputBorderColor', e.target.value)}
            placeholder="auto (cor do texto)"
            className="flex-1"
          />
        </div>

        {/* Input border radius slider */}
        <div>
          <label className="block text-sm font-medium text-neutral-200 mb-2">
            Border Radius: {themeConfig.inputBorderRadius ?? 12}px
          </label>
          <input
            type="range"
            min={0}
            max={24}
            value={themeConfig.inputBorderRadius ?? 12}
            onChange={(e) => handleChange('inputBorderRadius', Number(e.target.value))}
            className="w-full accent-primary-500"
          />
          <div className="flex justify-between text-xs text-neutral-500 mt-1">
            <span>0 (reto)</span>
            <span>24 (pill)</span>
          </div>
        </div>
      </div>

      {/* Reset button */}
      <button
        type="button"
        onClick={() => onUpdate(DEFAULT_THEME_CONFIG)}
        className="w-full mt-6 p-2 text-sm text-neutral-400 hover:text-white border border-dashed border-neutral-700 rounded-xl hover:border-neutral-600 transition-colors"
      >
        Restaurar tema padrao
      </button>
    </div>
  );
};
